import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { IMAGE_MODEL_OPTIONS, VIDEO_MODEL_OPTIONS } from '@/features/audiences/model';
import { AiPromptPanel } from '@/features/audiences/ui/AiPromptPanel';
import PlanImage from '@/shared/assets/images/Plan.png';
import { AutoAwesomeIcon } from '@/shared/ui/Icons';
import { SuccessBanner } from '@/shared/ui/SuccessBanner';

import { ProfileImageSection, VideoSection } from './components';
import { useProfileMediaTab } from './hooks/useProfileMediaTab';

export function ProfileMediaTab() {
  const { t } = useTranslation('audiences');

  const {
    photoAssetId,
    mouseOverAssetId,
    videoAssetId,
    isProcessingMedia,
    isProcessingImage,
    isRegeneratingImage,
    isGeneratingVideos,
    processingLabel,
    showBanner,
    bannerType,
    handleBannerClose,
    handleBannerGenerateVideos,
    imageMetadata,
    handleUploadClick,
    handleImageRegenerateClick,
    thumbnailMetadata,
    detailMetadata,
    handleVideoRegenerateClick,
    handleVideoGenerateClick,
    thumbnailFailed,
    detailFailed,
    thumbnailErrorMessage,
    detailErrorMessage,
    retryingVideoType,
    handleRetryVideo,
    fileInputRef,
    handleFileChange,
    imageModalOpen,
    setImageModalOpen,
    imagePrompt,
    setImagePrompt,
    selectedImageModel,
    setSelectedImageModel,
    handleImageSend,
    videoModalOpen,
    setVideoModalOpen,
    videoPrompt,
    setVideoPrompt,
    selectedVideoModel,
    setSelectedVideoModel,
    handleVideoSend,
  } = useProfileMediaTab();

  return (
    <>
      <div className="flex flex-col gap-6">
        <ProfileImageSection
          photoAssetId={photoAssetId}
          isProcessing={isProcessingImage}
          processingLabel={processingLabel}
          disabled={isProcessingMedia}
          onUploadClick={handleUploadClick}
          onRegenerateClick={handleImageRegenerateClick}
          metadata={imageMetadata}
        />

        {showBanner && bannerType && (
          <SuccessBanner
            title={
              bannerType === 'upload'
                ? t('audiences.profileMediaTab.imageUploadedSuccessfully')
                : t('audiences.profileMediaTab.imageGeneratedSuccessfully')
            }
            description={t('audiences.profileMediaTab.wouldYouLikeToAutomatically')}
            onClose={handleBannerClose}
            actions={
              <>
                <Button onClick={handleBannerGenerateVideos}>
                  <AutoAwesomeIcon className="size-4" />
                  {t('audiences.profileMediaTab.generateVideos')}
                </Button>
                <Button variant="outline" onClick={handleBannerClose}>
                  {t('audiences.profileMediaTab.donTGenerate')}
                </Button>
              </>
            }
          />
        )}

        {/* Not decorative: it is what separates the image block from the videos. */}
        <Separator decorative={false} />

        <VideoSection
          thumbnailVideoAssetId={mouseOverAssetId}
          detailVideoAssetId={videoAssetId}
          isProcessing={isProcessingMedia}
          processingLabel={processingLabel}
          onRegenerateClick={handleVideoRegenerateClick}
          onGenerateClick={handleVideoGenerateClick}
          thumbnailMetadata={thumbnailMetadata}
          detailMetadata={detailMetadata}
          thumbnailFailed={thumbnailFailed}
          detailFailed={detailFailed}
          thumbnailErrorMessage={thumbnailErrorMessage}
          detailErrorMessage={detailErrorMessage}
          retryingVideoType={retryingVideoType}
          onRetryVideo={(type) => void handleRetryVideo(type)}
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        onChange={(e) => {
          void handleFileChange(e);
        }}
      />

      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('audiences.profileMediaTab.reGenerateImage')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <AiPromptPanel
              description={t('audiences.profileMediaTab.describeThePhysicalTraitsYou')}
              placeholder={t('audiences.profileMediaTab.describeYourPersonaImage')}
              agentName={t('audiences.profileMediaTab.imageGenerator')}
              agentThumbnailUrl={PlanImage}
              promptValue={imagePrompt}
              onPromptChange={setImagePrompt}
              onSend={() => void handleImageSend()}
              onClose={() => setImageModalOpen(false)}
              selectedModel={selectedImageModel}
              onModelChange={setSelectedImageModel}
              isGenerating={isRegeneratingImage}
              modelOptions={IMAGE_MODEL_OPTIONS}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {videoAssetId || mouseOverAssetId
                ? t('audiences.profileMediaTab.reGenerateVideos')
                : t('audiences.profileMediaTab.generateVideos')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <AiPromptPanel
              description={t('audiences.profileMediaTab.addSpecificInstructionsForThe')}
              placeholder={t('audiences.profileMediaTab.describeYourPersonaVideos')}
              agentName={t('audiences.profileMediaTab.videoGenerator')}
              agentThumbnailUrl={PlanImage}
              promptValue={videoPrompt}
              onPromptChange={setVideoPrompt}
              onSend={() => void handleVideoSend()}
              onClose={() => setVideoModalOpen(false)}
              selectedModel={selectedVideoModel}
              onModelChange={setSelectedVideoModel}
              isGenerating={isGeneratingVideos}
              modelOptions={VIDEO_MODEL_OPTIONS}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
